'use client';

import React, { JSX, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import * as _ from 'underscore';

import { AppDispatch } from '@/store/store';

import { 
	productSearchInventory, 
	productSetNameValue, 
} from '@/actions/product';

import { 
	CSS_HEADER_HEIGHT,
	CSS_HEADER_NOTICE_HEIGHT, 
	CSS_SLIDE_DURATION, 
} from '@/constants/css';
import { 
	FV_KEY_AGGREGATIONS,
	FV_KEY_IS_SEALED, 
	FV_KEY_IS_BUYLIST,
	FV_KEY_LOCATIONS, 
	FV_KEY_NAME, 
	FV_KEY_PRODUCT_LINE,
	FV_KEY_SORT,
	FilterConfigProps,
	FilterOptionProps,
} from '@/constants/filters';
import { GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL } from '@/constants/gallery';
import { 
	ICON_CHEVRON_LEFT_DOUBLE,
	ICON_CHEVRON_RIGHT_DOUBLE,
	ICON_FILTER,
} from '@/constants/icons';
import { MKT_NOTICE_KEY_HEADER } from '@/constants/marketing';
import { PL_TYPES_ENABLED } from '@/constants/product';
import {
	TX_FILTER,
	TX_FILTER_APPLIED_FILTERS_ZERO,
	TX_FILTER_APPLIED_FILTERS_SINGLE,
	TX_FILTER_APPLIED_FILTERS_PLURAL,
	TX_FILTERS,
} from '@/constants/strings';
import {
	URL_BUY_GENERAL,
	URL_BUY_PRODUCT_LINE, 
	URL_SELL_GENERAL,
	URL_SELL_PRODUCT_LINE, 
} from '@/constants/urls';

import useFilters from '@/hooks/useFilters';
import useNotice from '@/hooks/useNotice';
import useProductLines from '@/hooks/useProductLines';
import useStore from '@/hooks/useStore';

import ProductLine from '@/models/product-lines/ProductLine';
import Product from '@/models/products/Product';

import { viewItemListEvent } from '@/utils/analytics';
import {
	getFilterConfigByKey, 
	getFilterConfigKeys, 
	getFilterQueryString,
	getFiltersFromUrlParams,
	getGeneralFilters, 
	getStaticFilterOptions, 
	hasStaticFilterOptions, 
	normalizeFilterValuesProductLines, 
} from '@/utils/filters';
import { 
	formatServerError, 
	stringFormat, 
} from '@/utils/formatting';
import {
	getGalleryDefaultPageSize, 
	getGalleryFilterStorageKey, 
} from '@/utils/gallery';
import {
	isVarArray, 
	isVarBool, 
	isVarNumber, 
	isVarString, 
} from '@/utils/general';
import { getOrderedProductLines } from '@/utils/product';
import { 
	getPageLimit, 
	getPageOffset, 
} from '@/utils/request';

import Gallery from '@/components/Gallery/Gallery';
import HeaderSearch from '@/components/Header/elements/HeaderSearch';
import Icon from '@/components/Icons/Icon';
import ProductFilters from '@/components/ProductSearch/blocks/ProductFilters';

import styles from './_styles/ProductSearch.module.scss';


// Prop types
interface ProductSearchProps {
	initialSearchQuery: { [k: string]: string | string[] | undefined };
	isBuylist: boolean;
	productLinePermalink?: string;
}

export const ProductSearch = (props: ProductSearchProps): JSX.Element => {
  
	// Dispatch
	const dispatch = useDispatch<AppDispatch>();

	// Constants
	const PRIMARY_FILTER_KEY = getGalleryFilterStorageKey(GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL);

	// Props
	const {
		initialSearchQuery,
		isBuylist,
		// productLinePermalink,  // This WILL change, use PL from URL path instead
	} = props;

	// Notice
	const { notice } = useNotice(MKT_NOTICE_KEY_HEADER);

	// Router
	const router = useRouter();

	// Pathname
	const pathname = usePathname();

	// Search params for query string change
	const sp = useSearchParams();

	// Store
	const { store } = useStore();

	// Accessing product lines
	const {
		lines: enabledLines,
		linesPending: enabledLinesPending,
		fetchLines: fetchEnabledLines,
	} = useProductLines(PL_TYPES_ENABLED);
	
	// Filters
	const {
		filters,
		filtersCustomOptions,
		setFilters,
		setFiltersCustomOptions,
	} = useFilters(PRIMARY_FILTER_KEY);

	// Local state
	const [filtersRetracted, setFiltersRetracted] = useState<boolean>(false);
	const [filtersMobileHidden, setFiltersMobileHidden] = useState<boolean>(true);
	const [filtersMobileLastToggle, setFiltersMobileLastToggle] = useState<number>(0);
	const [filtersMobileOpen, setFiltersMobileOpen] = useState<boolean>(false);
	const [filtersDesktopOpen, setFiltersDesktopOpen] = useState<boolean>(true);

	const [requestCount, setRequestCount] = useState<number>(0);
	const [requestData, setRequestData] = useState<Product[]>([]);
	const [requestError, setRequestError] = useState<string | null>(null);
	const [requestPending, setRequestPending] = useState<boolean>(false);
	const [requestMade, setRequestMade] = useState<boolean>(false);

	const [infiniteLoading, setInfiniteLoading] = useState<boolean>(false);
	const [gallerySignal, setGallerySignal] = useState<number>(0);
	const [transitionHappening, setTransitionHappening] = useState<boolean>(false);

	// Refs
	const checkFilterIntervalRef = useRef<NodeJS.Timeout | null>(null);
	const filtersDesktopRef = useRef<HTMLDivElement | null>(null);
	const filtersMobileRef = useRef<HTMLDivElement | null>(null);	
	const saveControllerRef = useRef<AbortController | null>(null);

	// Internationalization
	const { t } = useTranslation();

	// Derived constants
	const buylistFilters = { [FV_KEY_IS_BUYLIST]: isBuylist };

	// Methods
	const getFilterConfig = useCallback((plValue?: string) => {
		return ProductLine.getCustomFilters(plValue, { isBuylist: isBuylist }) || getGeneralFilters({ isBuylist: isBuylist });
	}, [ isBuylist ]);

	const storeLocationFitlerOptions = useCallback((): FilterOptionProps[] => {

		if(!store?.locations) { return []; }

		// If single location, do not show location filter
		if(store.locations.length <= 1) { return []; }

		const optionsResp: FilterOptionProps[] = [];

		// Add default locations first
		const defaultLocation = store.locations.find((l => l.isDefault === true));
		if(defaultLocation?.publicUuid) {
			optionsResp.push({
				display: `${defaultLocation.name}`,
				value: defaultLocation.publicUuid,
			});
		}

		for(const loc of store.locations) {
			if(loc.isDefault !== true && loc.publicUuid) {
				optionsResp.push({
					display: `${loc.name}`,
					value: loc.publicUuid,
				});
			}
		}
		return optionsResp;
	}, [ store?.locations ]);

	const getElementOptions = useCallback((filterElementConfig: FilterConfigProps, plValue?: string) => {

		const filterKey = filterElementConfig.key;

		// 1) Handle dynamic options
		if(filterElementConfig.dynamicOptions) {
			return filtersCustomOptions[filterElementConfig.key] || [];
		}

		// 2) Handle product line options
		if(filterKey === FV_KEY_PRODUCT_LINE) {
			return normalizeFilterValuesProductLines(getOrderedProductLines(enabledLines));
		} else if(filterKey === FV_KEY_LOCATIONS) {
			return storeLocationFitlerOptions();
		}

		// 3) Handle product line specific options
		const filterConfigs = { isBuylist: isBuylist };
		const filterPermalink = filters[FV_KEY_PRODUCT_LINE] || plValue || '';
		const specificOptions = ProductLine.getFilterOptions(filterKey, filterPermalink, filterConfigs);

		return specificOptions || [];

	}, [ filters[FV_KEY_PRODUCT_LINE], isBuylist, storeLocationFitlerOptions ]);

	const getFilterOptions = (plValue?: string) => {

		const filterConfig = getFilterConfig(plValue);
		const filterOptions: Record<string, FilterOptionProps[]> = {};

		for(const filterElementConfig of filterConfig) {
			if(hasStaticFilterOptions(filterElementConfig)) {
				filterOptions[filterElementConfig.key] = getStaticFilterOptions(filterElementConfig); 
			} else {
				filterOptions[filterElementConfig.key] = getElementOptions(filterElementConfig, plValue); 
			}
		}
		return filterOptions;
	}

	const removeFilter = (filterKey: string) => {

		const updatedFilters: Record<string, any> = {};
		for(const key in filters) {
			if(key !== filterKey) {
				updatedFilters[key] = filters[key];
			}
		}

		const prunedFilters = pruneFilters(updatedFilters);
		setFilters(prunedFilters);

		// Update URL with fitler query string
		const filterQueryString = getFilterQueryString(prunedFilters);

		if(filterKey === FV_KEY_PRODUCT_LINE) {
			window.history.replaceState(null, '', `${isBuylist ? URL_SELL_GENERAL : URL_BUY_GENERAL}${filterQueryString}`);
		} else {
			window.history.replaceState(null, '', `${pathname}${filterQueryString}`);
		}

		callApi(prunedFilters, 1, getGalleryDefaultPageSize(GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL), false);
	};

	const formatFiltersForReq = (filterObj: Record<string, any>) => {
	
		const respObj: Record<string, any> = {};

		for(const key in filterObj) {

			switch(key) {
				case FV_KEY_PRODUCT_LINE:
				
					// if empty, do not include in req
					if(filterObj[key].length === 0) {
						break;
					}

					// Product line should be array
					respObj[key] = [ filterObj[key] ];
					break;

				case FV_KEY_IS_SEALED:
					// is_sealed should be boolean
					// if more than one options selected, send nothing
					if(filterObj[key].length === 1) {
						respObj[key] = filterObj[key][0];
					}
					break;
				default:

					if(filterObj[key] === true || filterObj[key] === false) {
						// Handle booleans
						respObj[key] = filterObj[key];
					} else if(isVarNumber(filterObj[key])) {
						// Handle numeric values
						respObj[key] = filterObj[key];
					} else if(filterObj[key].length) {
						// Handles strings/arrays
						respObj[key] = filterObj[key];
					}
			}
		}

		// Add aggregation key for all filters that have said key
		const aggregations = [];
		for(const filterConf of getFilterConfig(filterObj[FV_KEY_PRODUCT_LINE])) {
			if(filterConf.aggregationKey) {
				aggregations.push(filterConf.aggregationKey);
			}
		}

		// if(aggregations.length > 0) {
		// 	respObj[FV_KEY_AGGREGATIONS] = aggregations;
		// }

		return respObj;
	}

	const callApi = useCallback(async (
		filterObj: Record<string, any>,
		pageNum: number, 
		pageSize: number, 
		infiniteScrollReq: boolean = false
	) => {
		
		// Cancel any existing requests
		if(saveControllerRef.current) {
			saveControllerRef.current.abort();
		}
		const controller = new AbortController();
		saveControllerRef.current = controller;

		try {

			if(infiniteScrollReq) {
				setInfiniteLoading(true);
			} else {
				setRequestError(null);
				setRequestPending(true);
			}

			const paginationData = {
				limit: getPageLimit(pageNum, pageSize),
				offset: getPageOffset(pageNum, pageSize),
			};

			const reqFilterObject = Object.assign({}, formatFiltersForReq(filterObj), paginationData);

			const resp = await dispatch(productSearchInventory(reqFilterObject, controller.signal));

			const newData = infiniteScrollReq 
				? requestData.concat(resp.data) 
				: resp.data;

			setRequestCount(resp.count);
			setRequestData(newData);
			setRequestError(null);
			setRequestPending(false);
			setRequestMade(true);
			setInfiniteLoading(false);

			// Fire view_item_list analytics event
			if(pageNum === 1) {
				viewItemListEvent(newData, {
					item_list_id: 'product_gallery',
					item_list_name: 'Product gallery', 
				});
			}
			
		} catch(errResp) {
			
			if(controller.signal.aborted) { return null; }
				
			setRequestCount(0);
			setRequestData([]);
			setRequestError(formatServerError(errResp));
			setRequestPending(false);
			setRequestMade(true);
			setInfiniteLoading(false);
		}
		
	}, [ dispatch, productSearchInventory, requestData ]);

	const pruneFilters = useCallback((unprunedFilters: Record<string, any>) => {
	
		const normalUpdate = { ...unprunedFilters };
		const newConfig = getFilterConfig(unprunedFilters[FV_KEY_PRODUCT_LINE]);

		const nonConfigKeys = [
			FV_KEY_NAME,
			FV_KEY_IS_BUYLIST,
			FV_KEY_SORT,
		];

		const newConfigKeys = nonConfigKeys;
		for(const filterElementConfig of newConfig) {
		
			const filterKeys = getFilterConfigKeys(filterElementConfig, { isBuylist: isBuylist });
			for(const key of filterKeys) {
				newConfigKeys.push(key);
			}
		}

		const prunedUpdate: Record<string, any> = {};
		for(const key in normalUpdate) {

			if(nonConfigKeys.includes(key)) {
				prunedUpdate[key] = normalUpdate[key]; 
			} else if(newConfigKeys.includes(key)) {

				const filterElementConfig = getFilterConfigByKey(key);

				if(filterElementConfig && !hasStaticFilterOptions(filterElementConfig)) {

					const options = getElementOptions(filterElementConfig); 
					const optionValues = [];
					for(const optionElement of options) {
						optionValues.push(optionElement.value);
					}

					const normalUpdateValue = normalUpdate[key];

					if(isVarString(normalUpdateValue)) {
						if(optionValues.includes(normalUpdateValue)) {
							prunedUpdate[key] = normalUpdateValue;
						}
					} else if(isVarArray(normalUpdateValue)) {
						
						const prunedValues = [];
						for(const normalValue of normalUpdateValue) {
							if((isVarString(normalValue) || isVarNumber(normalValue) || isVarBool(normalValue)) && optionValues.includes(normalValue)) {
								prunedValues.push(normalValue);
							}
						}
						prunedUpdate[key] = prunedValues;
					}

				} else {
					prunedUpdate[key] = normalUpdate[key]; 
				}
			}
		}
		return prunedUpdate;
	}, [ filters, getElementOptions, getFilterConfig, isBuylist ]);

	// This is the function passed to both filters and gallery;
	// Add search/filter logic should be handled here, and callApi just handles the API call
	// Slightly different than the gallery in AddProduct where callApi handles both
	const updateFilters = useCallback((
		filterObj: Record<string, any>,
		pageNum: number = 1, 
		pageSize: number = getGalleryDefaultPageSize(GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL), 
		infiniteScrollReq: boolean = false
	) => {

		// Prune and set filters
		const updatedFilters = pruneFilters(Object.assign({}, filters, filterObj));
		for(const key in filterObj) {
			if(!filterObj[key] || (isVarArray(filterObj[key]) && filterObj[key].length === 0)) {
				delete updatedFilters[key];
			}
		}
		
		setFilters(updatedFilters);

		// Update URL with fitler query string
		const filterQueryString = getFilterQueryString(updatedFilters);

		if(pathname && !pathname.endsWith(updatedFilters[FV_KEY_PRODUCT_LINE])) {
			if(updatedFilters[FV_KEY_PRODUCT_LINE]) {
				const newUrl = stringFormat(isBuylist ? URL_SELL_PRODUCT_LINE : URL_BUY_PRODUCT_LINE, {
					productLine: updatedFilters[FV_KEY_PRODUCT_LINE],
				}) + filterQueryString;
				window.history.replaceState(null, '', newUrl);
			} else {
				window.history.replaceState(null, '', `${isBuylist ? URL_SELL_GENERAL : URL_BUY_GENERAL}${filterQueryString}`);
			}
		} else {
			window.history.replaceState(null, '', `${pathname}${filterQueryString}`);
		}

		callApi(updatedFilters, pageNum, pageSize, infiniteScrollReq);

	}, [ pruneFilters, isBuylist, callApi, setFilters, filters, router, pathname ]);

	const updateCustomOptions = (optionObj: Record<string, any[]>) => {
		try {

			const transformedOptions: Record<string, FilterOptionProps[]> = {};
			for(const key in optionObj) {
				const optionsArray = [];
				for(const opt of optionObj[key]) {
					if(opt.toOption) {
						optionsArray.push(opt.toOption());
					}
				}
				transformedOptions[key] = optionsArray;
			}

			setFiltersCustomOptions({ ...filtersCustomOptions, ...transformedOptions });

		} catch(err) {
			console.error(err);
		}
	};
	
	const signalGallery = () => {

		setGallerySignal(Date.now());
		setTransitionHappening(false);

		filtersDesktopRef?.current?.removeEventListener('transitionend', signalGallery, false);
	};

	const toggleDesktopFilters = () => {
		
		filtersDesktopRef?.current?.addEventListener('transitionend', signalGallery, false);

		if(window && filtersDesktopOpen) {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}

		setFiltersDesktopOpen((prevState) => !prevState);
		setTransitionHappening(true);
	};

	const toggleMobileFilters = ()=> {

		if(Date.now() < (CSS_SLIDE_DURATION + filtersMobileLastToggle)) {
			return null;
		}

		setFiltersMobileOpen((prevState) => !prevState);
		setFiltersMobileLastToggle(Date.now());

		if(filtersMobileOpen) {
			// Disable body scroll
			document.body.style.overflow = 'hidden';
		} else {
			// Enable body scroll
			document.body.style.overflow = 'visible';
		}

	};

	const getToggleStyle = (): React.CSSProperties => {
		
		if(filtersDesktopOpen || transitionHappening) { return {}; }

		const ARROW_MARGIN = 10;

		const headerOffset = notice ? CSS_HEADER_HEIGHT + CSS_HEADER_NOTICE_HEIGHT : CSS_HEADER_HEIGHT;

		return { 
			position: 'fixed',
			right: 'unset',
			left: `${ARROW_MARGIN}px`,
			top: `${headerOffset + ARROW_MARGIN}px` 
		};
	};

	const getAppliedLabel = (count: number) => {
		if(count === 0) {
			return TX_FILTER_APPLIED_FILTERS_ZERO;
		}
		if(count === 1) {
			return TX_FILTER_APPLIED_FILTERS_SINGLE;
		} else {
			return TX_FILTER_APPLIED_FILTERS_PLURAL;
		}
	}

	const getFilterCount = () => {
		
		if(!filters) { return 0; }

		const appliedFilters = [];

		for(const conf of getFilterConfig(filters[FV_KEY_PRODUCT_LINE])) {
			const filterKey = conf.key;
			if(filters[filterKey] && filters[filterKey].length) {
				appliedFilters.push(filterKey);
			}
		}
		return appliedFilters.length;
	}

	const filterCount = getFilterCount();

	const checkSlidingFilters = () => {
	
		// Check desktop
		if(filtersDesktopRef.current) {

			try {
				const positionRect = filtersDesktopRef.current.getBoundingClientRect();
				const retracted = (positionRect.width + positionRect.x) === 0;

				setFiltersRetracted(retracted);

			} catch(errResp) {
				console.error(errResp);
			}
		}

		// Check mobile
		if(filtersMobileRef.current) {

			try {
				const positionRectMobile = filtersMobileRef.current.getBoundingClientRect();
				const retractedMobile = positionRectMobile.height === 0;

				if(filtersMobileHidden !== retractedMobile) {
					setFiltersMobileHidden(retractedMobile);
					if(retractedMobile && filtersMobileOpen === true) {
						setFiltersMobileOpen(false);
					}
				}
			} catch(errResp) {
				console.error(errResp);
			}
		}
	}

	const filterInitialSelections = (existingFilters: Record<string, any>) => {
		
		// Loop through filters to be displayed and extract initSelection data
		const initSelections: Record<string, any> = {};
		const filterConfigs = getFilterConfig(existingFilters[FV_KEY_PRODUCT_LINE]);

		for(const fc of filterConfigs) {
			if(fc.initSelection && !Object.keys(existingFilters).includes(fc.key)) {
				initSelections[fc.key] = fc.initSelection;
			}
		}
		return initSelections;
	}

	const productLinePermalinkFromPath = useCallback((): string => {
		
		if(!pathname || !window) { return ''; }

		const pn = window?.location?.pathname;

		const generalPath = isBuylist ? URL_SELL_GENERAL : URL_BUY_GENERAL;

		if(pn === generalPath) {
			return '';
		} else {
			return pn.replace(`${generalPath}/`, '');
		}
	}, [ pathname, isBuylist ]);

	const getFiltersFromUrl = useCallback(() => {

		const paramFilters = getFiltersFromUrlParams(sp, { isBuylist });

		const permalink = productLinePermalinkFromPath();

		// Get product line from URL if exists
		if(permalink) {

			if(!enabledLinesPending && enabledLines.length > 0) {
				for(const pl of enabledLines) {
					if(pl.permalink === permalink) {
						if(isBuylist && !pl.hasBuylist) {
							continue;
						}
						paramFilters[FV_KEY_PRODUCT_LINE] = permalink;
						break;
					}
				}
				if(!paramFilters[FV_KEY_PRODUCT_LINE]) {
					window.history.replaceState(null, '', isBuylist ? URL_SELL_GENERAL : URL_BUY_GENERAL);
				}
			} else {
				// If product lines not loaded, assume correct and check again when API returns
				paramFilters[FV_KEY_PRODUCT_LINE] = permalink;
			}
		}

		// Check any existing filters
		const initialFilters = filterInitialSelections(paramFilters);
		
		const prundedFilters = pruneFilters(Object.assign({}, paramFilters, initialFilters, buylistFilters));

		return prundedFilters;

	}, [ productLinePermalinkFromPath, enabledLinesPending, enabledLines, pruneFilters, sp ]);


	// Effects
	useEffect(() => {
		
		// Call a search with initial filters
		const initialFilters = getFiltersFromUrl();

		setFilters(initialFilters);

		callApi(initialFilters, 1, getGalleryDefaultPageSize(GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL));

		checkFilterIntervalRef.current = setInterval(checkSlidingFilters, 50);

		return () => {
			
			// Clear intervals
			if(checkFilterIntervalRef.current) {
				clearInterval(checkFilterIntervalRef.current);
			}

			// Abort pending queries
			if(saveControllerRef.current) {
				saveControllerRef.current.abort();
			}

			// Clear name search
			dispatch(productSetNameValue(''));

			// Check for transition listeners
			filtersDesktopRef?.current?.removeEventListener('transitionend', signalGallery, false);

			// Enable body scroll
			document.body.style.overflow = 'visible';
		};

	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		
		// If URL query params change, update filters
		const filtersClone = { ...filters };
		const updatedFilters = getFiltersFromUrl();
		const updatedFiltersSansProductLine = { ...updatedFilters };

		delete filtersClone[FV_KEY_PRODUCT_LINE];
		delete updatedFiltersSansProductLine[FV_KEY_PRODUCT_LINE];

		if(!_.isEqual(filtersClone, updatedFiltersSansProductLine)) {
			setFilters(updatedFilters);
			callApi(updatedFilters, 1, getGalleryDefaultPageSize(GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL));
		}

	}, [ sp.toString() ]);

	useEffect(() => {

		const updatedFilters = getFiltersFromUrl();

		if(!_.isEqual(filters, updatedFilters)) {
			setFilters(updatedFilters);
			callApi(updatedFilters, 1, getGalleryDefaultPageSize(GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL));
		}
	}, [ pathname ]);

	useEffect(() => {
		fetchEnabledLines();
	}, [ fetchEnabledLines ]);
	
	useEffect(() => {

		if(requestMade) {

			if(filters[FV_KEY_NAME]) {
				
				callApi(filters, 1, getGalleryDefaultPageSize(GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL), false);

				const filterQueryString = getFilterQueryString(filters);
				window.history.replaceState(null, '', `${pathname}${filterQueryString}`);

			} else if(Object.keys(filters).includes(FV_KEY_NAME)) {
				removeFilter(FV_KEY_NAME);
			}
		}
			
	}, [ filters[FV_KEY_NAME], requestMade ]);

	// Render
	return (
		<div className={`${styles.ProductSearch} ${notice ? styles.notified : ''}`}>
			<div className={`${styles.productSearchWrapper} ${!filtersDesktopOpen ? styles.toggled : ''}`}>
				<div
					ref={filtersDesktopRef} 
					className={`${styles.psFiltersWrapper} ${!filtersDesktopOpen ? styles.closed : ''}`}>
					{filtersRetracted === false && (
						<ProductFilters
							config={getFilterConfig(filters[FV_KEY_PRODUCT_LINE])}
							loading={enabledLinesPending}
							filters={filters}
							filterOptions={getFilterOptions(filters[FV_KEY_PRODUCT_LINE])}
							isBuylist={isBuylist}
							setFilter={updateFilters}
							setCustomFilterOptions={updateCustomOptions}
							removeFilter={removeFilter} />
					)}
					<div 
						className={`${styles.psFiltersToggle} ${!filtersDesktopOpen ? styles.closed : ''}`} 
						onClick={toggleDesktopFilters}
						style={getToggleStyle()}>
						<Icon value={filtersDesktopOpen ? ICON_CHEVRON_LEFT_DOUBLE : ICON_CHEVRON_RIGHT_DOUBLE} />
					</div>
				</div>

				<div className={`${styles.psGalleryWrapper} ${!filtersDesktopOpen ? styles.toggled : ''}`}>
					<div ref={filtersMobileRef} className={styles.psGalleryMobileFilters}>
						<div className={styles.psmVisible}>
							<div className={styles.psmVisibleFilterToggle} onClick={toggleMobileFilters}>
								<div className={styles.psmIcon}>
									<Icon 
										value={ICON_FILTER} 
										iconClass={styles.fullMenuIconWrapper} />
								</div>
								<div className={styles.psmLabel}>{t(TX_FILTERS)}</div>
							</div>
							<div className={styles.psmVisibleApplied}>
								{filterCount > 0 && (
									<span className={styles.appliedNumeral}>{filterCount}</span>
								)}
								<span>&nbsp;{t(getAppliedLabel(filterCount), {count: filterCount})}</span>
							</div>
							<div className={styles.psmVisibleSearch}>
								<HeaderSearch />
							</div>
						</div>
						<div className={`${styles.psmDrawer} ${filtersMobileOpen ? styles.open : ''}`}>
							<div className={styles.psmDrawerFilterWrapper}>
								{filtersMobileHidden === false && (
									<ProductFilters
										config={getFilterConfig(filters[FV_KEY_PRODUCT_LINE])}
										loading={enabledLinesPending}
										filters={filters}
										filterOptions={getFilterOptions(filters[FV_KEY_PRODUCT_LINE])}
										isBuylist={isBuylist}
										setFilter={updateFilters}
										setCustomFilterOptions={updateCustomOptions}
										removeFilter={removeFilter} />
								)}
							</div>
							<div className={styles.psmDrawerFooterWrapper}>
								<div>
									<button type='button' className={styles.psmFooterAction} onClick={toggleMobileFilters}>{t(TX_FILTER)}</button>
								</div>
							</div>
						</div>
					</div>
					<div className={styles.psGallery}>
						<Gallery 
							config={GALLERY_CONFIG_PRODUCT_SEARCH_GENERAL}
							callApi={updateFilters}
							data={requestData}
							dataCount={requestCount} 
							dataLoading={requestPending}
							filters={filters}
							infiniteLoading={infiniteLoading}
							isBuylist={isBuylist}
							selectAction={() => null}
							signal={gallerySignal} />
					</div>
				</div>
			</div>
		</div>
	);
};

export default ProductSearch;


